'use client';

import React, { useState, useEffect, useRef } from 'react';
import { useChat } from '@/lib/hooks/useChat';
import { toast } from 'sonner';

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

const SYSTEM_PROMPT = `أنت مساعد خدمة عملاء ذكي لمنصة عقار ماركت، منصة عقارية رائدة في الإمارات العربية المتحدة. دورك هو:

1. مساعدة المستخدمين في إدراج عقاراتهم مجاناً على المنصة
2. إرشاد المستخدمين حول كيفية التنقل واستخدام المنصة
3. الإجابة على الأسئلة حول كيفية عمل المنصة
4. شرح مميزات المنصة:
   - تصفح العقارات مجاني تماماً
   - إضافة وإدراج العقارات مجاني
   - بيانات التواصل (أرقام الهاتف) تتطلب رسوماً رمزية صغيرة
   - المنصة تغطي جميع إمارات الدولة: دبي، أبوظبي، الشارقة، عجمان، رأس الخيمة، الفجيرة، أم القيوين
   - العملة المستخدمة هي الدرهم الإماراتي (AED)
5. مساعدة المستخدمين في لوحة إدارة العقارات
6. الإجابة على الأسئلة المتعلقة بالحساب

معلومات أساسية عن المنصة:
- تصفح العقارات: /property-listings
- إدارة إعلاناتك: /property-management
- تسجيل الدخول / إنشاء حساب: /sign-up-login-screen
- إدراج العقار مجاني تماماً
- عرض بيانات التواصل يتطلب رسوماً رمزية

المناطق المتاحة في الإمارات:
- دبي: دبي مارينا، نخلة جميرا، وسط مدينة دبي، الجميرا، جبل علي، بر دبي
- أبوظبي: جزيرة السعديات، جزيرة الريم، مدينة محمد بن زايد، الخالدية
- الشارقة: حي الجامعة، النهدة، القاسمية
- عجمان، رأس الخيمة، الفجيرة

كن ودوداً ومختصراً ومفيداً. إذا سُئلت عن أسعار أو توفر عقارات محددة، اشرح أنك تستطيع توجيههم إلى صفحة العقارات. شجع المستخدمين دائماً على استكشاف المنصة.`;

const QUICK_SUGGESTIONS = [
  'كيف أضيف عقاري؟',
  'كيف تعمل المنصة؟',
  'هل الإدراج مجاني؟',
  'كيف أعرض بيانات التواصل؟',
];

export default function AIChatbot() {
  const [isOpen, setIsOpen] = useState(false);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState<Message[]>([
    {
      role: 'assistant',
      content: 'مرحباً! 👋 أنا مساعد عقار ماركت الذكي. يمكنني مساعدتك في إدراج عقارك، التنقل في المنصة، أو الإجابة على أي أسئلة. كيف يمكنني مساعدتك اليوم؟',
    },
  ]);
  const [hasNewMessage, setHasNewMessage] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const { response, isLoading, error, sendMessage } = useChat(
    'GEMINI',
    'gemini/gemini-2.5-flash',
    true
  );

  useEffect(() => {
    if (error) toast.error(error.message);
  }, [error]);

  // Track streaming response
  const lastResponseRef = useRef('');
  const streamingMessageAddedRef = useRef(false);

  useEffect(() => {
    if (!response) return;

    if (isLoading) {
      // Streaming in progress — update or add streaming message
      if (!streamingMessageAddedRef.current) {
        streamingMessageAddedRef.current = true;
        setMessages(prev => [...prev, { role: 'assistant', content: response }]);
      } else {
        setMessages(prev => {
          const updated = [...prev];
          updated[updated.length - 1] = { role: 'assistant', content: response };
          return updated;
        });
      }
      lastResponseRef.current = response;
    } else if (response !== lastResponseRef.current) {
      // Streaming done — finalize
      setMessages(prev => {
        const updated = [...prev];
        if (updated[updated.length - 1]?.role === 'assistant') {
          updated[updated.length - 1] = { role: 'assistant', content: response };
        } else {
          updated.push({ role: 'assistant', content: response });
        }
        return updated;
      });
      lastResponseRef.current = response;
      streamingMessageAddedRef.current = false;
      if (!isOpen) setHasNewMessage(true);
    }
  }, [response, isLoading, isOpen]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (isOpen) {
      setHasNewMessage(false);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  const handleSend = (text?: string) => {
    const messageText = text || input.trim();
    if (!messageText || isLoading) return;

    const userMessage: Message = { role: 'user', content: messageText };
    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    setInput('');
    streamingMessageAddedRef.current = false;
    lastResponseRef.current = '';

    const apiMessages = [
      { role: 'system' as const, content: SYSTEM_PROMPT },
      ...updatedMessages.map(m => ({ role: m.role as 'user' | 'assistant', content: m.content })),
    ];

    sendMessage(apiMessages, { temperature: 0.7, max_tokens: 1024 });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <>
      {/* Floating Button */}
      <div className="fixed bottom-6 left-6 z-50 flex flex-col items-center gap-2">
        {/* Tooltip label */}
        {!isOpen && (
          <div className="bg-gray-800 text-white text-xs font-500 px-2.5 py-1 rounded-lg shadow-md whitespace-nowrap">
            مساعد ذكي
            <div className="absolute bottom-[-4px] left-1/2 -translate-x-1/2 w-2 h-2 bg-gray-800 rotate-45" />
          </div>
        )}
        <div className="relative">
          {/* Pulse ring */}
          {!isOpen && (
            <span className="absolute inset-0 rounded-full bg-primary-400 opacity-60 animate-ping" />
          )}
          <button
            onClick={() => setIsOpen(prev => !prev)}
            className="relative w-14 h-14 rounded-full bg-primary-500 hover:bg-primary-600 text-white shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center"
            aria-label="فتح المساعد الذكي"
          >
            {isOpen ? (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
              </svg>
            )}
            {hasNewMessage && !isOpen && (
              <span className="absolute top-0 right-0 w-3 h-3 bg-accent-500 rounded-full border-2 border-white" />
            )}
          </button>
        </div>
      </div>

      {/* Chat Window */}
      {isOpen && (
        <div
          className="fixed bottom-24 left-4 right-4 sm:right-auto sm:left-6 z-50 sm:w-96 bg-white rounded-2xl shadow-modal border border-gray-100 flex flex-col overflow-hidden"
          style={{ maxHeight: '520px', height: '520px' }}
        >
          {/* Header */}
          <div className="bg-primary-500 px-4 py-3 flex items-center gap-3 flex-shrink-0">
            <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white font-600 text-sm leading-tight">مساعد عقار ماركت</p>
              <p className="text-white/70 text-xs">مدعوم بالذكاء الاصطناعي Gemini</p>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="text-white/70 hover:text-white transition-colors p-1"
              aria-label="إغلاق"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3 scrollbar-hide" dir="rtl">
            {messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === 'user' ? 'justify-start' : 'justify-end'}`}>
                {msg.role === 'user' && (
                  <div className="w-7 h-7 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0 ml-2 mt-0.5">
                    <svg className="w-4 h-4 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                )}
                <div
                  className={`max-w-[75%] px-3 py-2 rounded-2xl text-sm leading-relaxed ${
                    msg.role === 'user' ?'bg-primary-500 text-white rounded-bl-sm' :'bg-gray-100 text-gray-800 rounded-br-sm'
                  }`}
                >
                  {msg.content}
                  {msg.role === 'assistant' && isLoading && i === messages.length - 1 && (
                    <span className="inline-flex gap-0.5 mr-1">
                      <span className="w-1 h-1 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="w-1 h-1 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                      <span className="w-1 h-1 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </span>
                  )}
                </div>
                {msg.role === 'assistant' && (
                  <div className="w-7 h-7 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0 mr-2 mt-0.5">
                    <svg className="w-4 h-4 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                    </svg>
                  </div>
                )}
              </div>
            ))}

            {/* Loading indicator when no streaming yet */}
            {isLoading && !streamingMessageAddedRef.current && (
              <div className="flex justify-end">
                <div className="bg-gray-100 px-3 py-2 rounded-2xl rounded-br-sm flex items-center gap-1">
                  <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Quick Suggestions */}
          {messages.length === 1 && (
            <div className="px-3 pb-2 flex flex-wrap gap-1.5 flex-shrink-0" dir="rtl">
              {QUICK_SUGGESTIONS.map((s) => (
                <button
                  key={s}
                  onClick={() => handleSend(s)}
                  disabled={isLoading}
                  className="text-xs px-2.5 py-1 rounded-full border border-primary-200 text-primary-600 hover:bg-primary-50 transition-colors disabled:opacity-50"
                >
                  {s}
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <div className="px-3 pb-3 pt-2 border-t border-gray-100 flex-shrink-0" dir="rtl">
            <div className="flex items-center gap-2 bg-gray-50 rounded-xl px-3 py-2">
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="اكتب سؤالك هنا..."
                disabled={isLoading}
                className="flex-1 bg-transparent text-sm text-gray-800 placeholder-gray-400 outline-none min-w-0"
              />
              <button
                onClick={() => handleSend()}
                disabled={!input.trim() || isLoading}
                className="flex-shrink-0 w-8 h-8 rounded-lg bg-primary-500 hover:bg-primary-600 disabled:bg-gray-200 text-white flex items-center justify-center transition-colors"
                aria-label="إرسال"
              >
                <svg className="w-4 h-4 rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
